import React, { useMemo } from "react";
import {
	Box,
	Typography,
	Chip,
	Button,
	Divider,
	Stack,
} from "@mui/material";
import FileOpenIcon from "@mui/icons-material/FileOpen";
import DownloadIcon from "@mui/icons-material/Download";
import LinkOutlined from "@mui/icons-material/LinkOutlined";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { useTranslation } from "react-i18next";
import Swal from "sweetalert2";

import ModalWrapper from "../ModalWrapper/ModalWrapper";
import DocCard from "../DocCard/DocCard";
import { SERVER_BASE_URL } from "../../config";
import { downloadDocument } from "../../services/documentService";

const DEFAULT_ATTACHMENT = { type: "none", value: "" };

const normalizeAttachment = (document) => {
	if (!document) return DEFAULT_ATTACHMENT;
	const type = String(document.attachment_type || "").toLowerCase();
	if (type === "file") {
		const value =
			document.path ||
			document.attachment ||
			(document.name ? `Documents/${document.name}` : "");
		return { type, value };
	}
	if (type === "url") {
		const value = document.path || document.attachment || document.url || "";
		return { type, value };
	}
	return DEFAULT_ATTACHMENT;
};

const getAssignedMachines = (document) => {
	if (!document) return [];
	if (
		Array.isArray(document.assigned_machines) &&
		document.assigned_machines.every((machine) => typeof machine === "object")
	) {
		return document.assigned_machines;
	}
	if (Array.isArray(document.machine_details)) {
		return document.machine_details;
	}
	return [];
};

const getCategories = (document) => {
	if (!document) return [];
	if (Array.isArray(document.categories)) return document.categories;
	return [];
};

const inferTypeLabel = (pathValue) => {
	const value = String(pathValue || "");
	const ext = value.includes(".") ? value.split(".").pop().toLowerCase() : "";
	if (ext === "pdf") return "PDF";
	if (ext === "png") return "PNG";
	if (ext === "jpg" || ext === "jpeg") return "JPG";
	if (ext === "csv") return "CSV";
	if (ext === "xls" || ext === "xlsx") return "Excel";
	if (ext === "doc" || ext === "docx") return "Word";
	if (ext === "ppt" || ext === "pptx") return "PPT";
	if (!ext) return "File";
	return ext.toUpperCase();
};

const DocumentViewModal = ({
	open,
	setOpen,
	document: documentData,
	onClose,
	onEdit,
	onDelete,
	width = "60vw",
}) => {
	const { t } = useTranslation();

	const attachment = useMemo(() => normalizeAttachment(documentData), [documentData]);
	const assignedMachines = useMemo(
		() => getAssignedMachines(documentData),
		[documentData]
	);
	const categories = useMemo(() => getCategories(documentData), [documentData]);

	const openIfReachable = async (url) => {
		if (!url) return;
		try {
			const response = await fetch(url, { method: "HEAD" });
			if (!response.ok) {
				throw new Error(`Status ${response.status}`);
			}
			window.open(url, "_blank", "noopener,noreferrer");
		} catch (error) {
			console.error("Unable to open document url", error);
			Swal.fire({
				icon: "error",
				title: t("ALERT_MESSAGES.ERROR"),
				text: t("ALERT_MESSAGES.UNABLE_TO_OPEN_DOCUMENT") || "Unable to open the document. Please try again later.",
			});
		}
	};

	const handleClose = () => {
		if (onClose) onClose();
	};

	const handleDownload = async () => {
		if (!documentData) return;
		try {
			const filename =
				(attachment.value || "").split("/").pop() ||
				documentData.name ||
				"document";
			const response = await downloadDocument(filename);
			const blob = new Blob([response.data], {
				type: "application/octet-stream",
			});
			const link = window.document.createElement("a");
			link.href = URL.createObjectURL(blob);
			link.download = filename;
			window.document.body.appendChild(link);
			link.click();
			window.document.body.removeChild(link);
		} catch (error) {
			Swal.fire({
				icon: "error",
				title: t("ALERT_MESSAGES.ERROR"),
				text: t("ALERT_MESSAGES.FAILED_TO_DOWNLOAD_FILE_PLEASE_TRY_AGAING"),
			});
		}
	};

	const handleVisitLink = () => {
		if (attachment.type === "url" && attachment.value) {
			window.open(attachment.value, "_blank", "noopener,noreferrer");
		}
	};

	const renderAttachmentPreview = () => {
		if (!documentData) return null;
		if (attachment.type === "file" && attachment.value) {
			const href = `${SERVER_BASE_URL}/${attachment.value}`;
			return (
				<DocCard
					title={documentData.name || t("TABLE_HEADERS.DOCUMENT_NAME")}
					href={href}
					onLinkClick={() => openIfReachable(href)}
					subtitle={t("BUTTON.SAVED") || "Saved"}
					chipLabel={inferTypeLabel(attachment.value)}
					imageProps={{ path: attachment.value }}
				/>
			);
		}
		if (attachment.type === "url" && attachment.value) {
			return (
				<DocCard
					title={documentData.name || t("LABELS.DOCUMENT_URL")}
					subtitle={attachment.value}
					href={attachment.value}
					onLinkClick={handleVisitLink}
					chipLabel="URL"
					imageProps={{ url: attachment.value }}
				/>
			);
		}
		return (
			<Typography variant="body2" color="text.secondary">
				{t("UI_TEXT.NO_ATTACHMENTS") || "No attachment"}
			</Typography>
		);
	};

	const actionButtons = (
		<>
			<Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
				<Box sx={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center', columnGap: 1.5 }}>
					<Button variant="outlined" onClick={handleClose}>
						{t("BUTTON.CLOSE")}
					</Button>
					{onDelete && documentData && (
						<Button
							variant="contained"
							color="error"
							startIcon={<DeleteIcon />}
							onClick={() => onDelete(documentData)}
						>
							{t("BUTTON.DELETE")}
						</Button>
					)}
				</Box>
				<Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', columnGap: 1.5 }}>
					{attachment.type === "file" && attachment.value && (
						<>
							<Button
								variant="contained"
								color="primary"
								startIcon={<FileOpenIcon />}
								onClick={() =>
									openIfReachable(`${SERVER_BASE_URL}/${attachment.value}`)
								}
							>
								{t("BUTTON.VIEW") || "View"}
							</Button>
							<Button
								variant="contained"
								color="primary"
								startIcon={<DownloadIcon />}
								onClick={handleDownload}
							>
								{t("UI_TEXT.DOWNLOAD")}
							</Button>
						</>
					)}
					{attachment.type === "url" && attachment.value && (
						<Button
							startIcon={<LinkOutlined />}
							variant="contained"
							color="primary"
							onClick={handleVisitLink}
						>
							{t("BUTTON.OPEN_LINK") || "Open Link"}
						</Button>
					)}
					{onEdit && documentData && (
						<Button
							variant="contained"
							color="primary"
							startIcon={<EditIcon />}
							onClick={() => onEdit(documentData)}
						>
							{t("BUTTON.EDIT")}
						</Button>
					)}

				</Box>
			</Box>
		</>
	);

	return (
		<ModalWrapper
			open={open}
			setOpen={setOpen}
			onClose={handleClose}
			title={documentData?.name || t("TABLE_HEADERS.DOCUMENT_NAME")}
			width={width}
			content={
				documentData ? (
					<Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
						<Box>
							<Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5 }}>
								{t("TABLE_HEADERS.DESCRIPTION")}
							</Typography>
							<Box
								sx={{
									p: 1.5,
									border: "1px solid #eee",
									borderRadius: 1,
									bgcolor: "background.default",
									maxHeight: "300px",
									overflowY: "auto",
									minHeight: "300px",
								}}
							>
								<div
									dangerouslySetInnerHTML={{
										__html: documentData.description || "",
									}}
								/>
							</Box>
						</Box>

						<Box>
							<Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5 }}>
								{t("LABELS.ASSIGNED_TO_MACHINES")}
							</Typography>
							{assignedMachines.length > 0 ? (
								<Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
									{assignedMachines.map((machine) => (
										<Chip
											key={machine.machine_id || machine.machine_number || machine}
											label={`${machine.machine_number || machine.machine_id || ""} ${machine.machine_name ? `- ${machine.machine_name}` : ""
												}`}
											size="small"
										/>
									))}
								</Box>
							) : (
								<Typography variant="body2" color="text.secondary">
									{t("UI_TEXT.NO_MACHINES_ASSIGNED") ||
										"No machine(s) assigned for this site"}
								</Typography>
							)}
						</Box>

						{!!categories.length && (
							<Box>
								<Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5 }}>
									{t("LABELS.CATEGORY")}
								</Typography>
								<Stack direction="row" spacing={1} flexWrap="wrap">
									{categories.map((category) => (
										<Chip
											key={category.category_id || category.id || category.category_name}
											label={category.category_name || category.label}
											size="small"
											variant="outlined"
										/>
									))}
								</Stack>
							</Box>
						)}

						<Box>
							<Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5 }}>
								{t("TABLE_HEADERS.STATUS")}
							</Typography>
							<Chip
								label={
									(documentData.visibility_status || "private") === "public"
										? t("LABELS.PUBLIC") || "Public"
										: t("LABELS.PRIVATE") || "Private"
								}
								color={documentData.visibility_status === "public" ? "success" : "default"}
								variant="outlined"
								size="small"
							/>
						</Box>

						<Divider />

						<Box>
							<Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5 }}>
								{t("LABELS.ATTACHMENT", { defaultValue: "Attachment" })}
							</Typography>
							{renderAttachmentPreview()}
						</Box>
					</Box>
				) : null
			}
			actions={actionButtons}
		/>
	);
};

export default DocumentViewModal;
